import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { prospectDate } from '@/utils/prospectTime';

const REASONS = {
  no_show: [['no_join', 'Não entrou na reunião'], ['no_answer', 'Não atendeu o telefone'], ['warned', 'Avisou que não podia'], ['other', 'Outro']],
  cancelled: [['warned', 'Avisou que não podia'], ['no_answer', 'Não atendeu o telefone'], ['no_join', 'Não entrou na reunião'], ['other', 'Outro']],
  reschedule: [['company_request', 'A pedido da empresa'], ['our_request', 'A pedido da Trabadju'], ['other', 'Outro']],
};
const TITLES = { no_show: '🚫 Empresa não compareceu', cancelled: '❌ Cancelada pela empresa', reschedule: '🔁 Remarcar' };
const field = 'w-full rounded-md border border-slate-300 px-3 py-2 text-sm';

export const meetingPassed = (p) => {
  const start = prospectDate(p.meeting?.start_at);
  if (!start || start.getTime() + (p.meeting.duration_min || 30) * 60000 > Date.now()) return false;
  if (p.has_report !== undefined) return !p.has_report;
  const done = prospectDate(p.last_meeting_completed_at);
  return !(done && done.getTime() >= start.getTime() - 12 * 3600000);
};

const errText = (e) => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : d?.message || 'Erro'; };

export const RescheduleComposer = ({ pid, company, onClose, onSent }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { api.get(`/admin/prospects/${pid}/reschedule-compose`).then((r) => setForm(r.data)).catch((e) => { toast.error(errText(e)); onClose(); }); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    setBusy(true);
    try { await api.post(`/admin/prospects/${pid}/reschedule-proposal`, { subject: form.subject, body: form.body }); toast.success('Nova data proposta — enviado de info@trabadju.cv'); onSent?.(); onClose(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="reschedule-composer">
        <DialogHeader><DialogTitle>Propor nova data a {company}</DialogTitle><DialogDescription>De: info@trabadju.cv · Para: {form?.to || '…'}</DialogDescription></DialogHeader>
        {!form ? <p className="text-sm text-slate-500">A preparar…</p> : <div className="space-y-2">
          {form.halted && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="reschedule-halted-warning">Ficha parada ({form.halted.reason}) — os emails automáticos estão desligados. Este email é enviado só porque o envia à mão.</p>}
          {form.unsubscribed && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">A empresa cancelou a subscrição de emails — não é possível enviar.</p>}
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} maxLength={200} className={`${field} h-10`} data-testid="reschedule-subject" />
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={9} maxLength={8000} className={field} data-testid="reschedule-body" />
          <p className="text-xs text-slate-600" data-testid="reschedule-slots">Horários incluídos (hora de Cabo Verde, 1 clique marca a reunião): <strong>{form.slots.join(' · ') || 'sem horários livres'}</strong></p>
          <p className="text-xs text-slate-500">Inclui também o botão «Registar a minha empresa» e a assinatura: {form.signature}</p>
        </div>}
        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={busy} onClick={onClose} data-testid="reschedule-cancel">Agora não</Button>
          <Button onClick={send} disabled={busy || !form || form.unsubscribed || form.body.trim().length < 5} data-testid="reschedule-send"><Send className="h-4 w-4 mr-1" />{busy ? 'A enviar…' : 'Enviar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const OutcomeDialog = ({ p, outcome, onClose, onDone }) => {
  const [f, setF] = useState({ reason: '', reason_other: '', note: '' });
  const [busy, setBusy] = useState(false);
  const needReason = outcome !== 'reschedule';
  const save = async () => {
    setBusy(true);
    try { await api.post(`/admin/prospects/${p.id}/meeting-outcome`, { outcome, reason: f.reason || null, reason_other: f.reason_other || null, note: f.note || null }); toast.success('Registado no histórico da ficha'); onDone(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="outcome-dialog">
        <DialogHeader><DialogTitle>{TITLES[outcome]}</DialogTitle><DialogDescription>{p.company}</DialogDescription></DialogHeader>
        <label className="text-sm font-semibold">Justificação{needReason ? '' : ' (opcional)'}</label>
        <select value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className={`${field} h-10`} data-testid="outcome-reason">
          <option value="">— escolher —</option>
          {REASONS[outcome].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {f.reason === 'other' && <input value={f.reason_other} onChange={(e) => setF({ ...f, reason_other: e.target.value })} maxLength={300} placeholder="Descreva o motivo" className={`${field} h-10`} data-testid="outcome-reason-other" />}
        <textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} rows={3} maxLength={2000} placeholder="Nota livre (opcional)" className={field} data-testid="outcome-note" />
        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={busy} onClick={onClose} data-testid="outcome-cancel">Cancelar</Button>
          <Button onClick={save} disabled={busy || (needReason && !f.reason) || (f.reason === 'other' && !f.reason_other.trim())} data-testid="outcome-save">{busy ? 'A guardar…' : 'Guardar e propor nova data'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MeetingOutcome = ({ p, onChanged }) => {
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState(null);
  const [composer, setComposer] = useState(false);
  if (!meetingPassed(p)) return null;
  const at = encodeURIComponent(p.meeting.start_at);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`meeting-outcome-${p.id}`}>
      <span className="text-xs font-bold text-slate-700">Como correu a reunião?</span>
      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate(`/admin/prospects/${p.id}/meetings?new=1&at=${at}`)} data-testid={`outcome-held-${p.id}`}>✅ Realizada</Button>
      <Button size="sm" variant="outline" onClick={() => setOutcome('no_show')} data-testid={`outcome-no-show-${p.id}`}>🚫 Não compareceu</Button>
      <Button size="sm" variant="outline" onClick={() => setOutcome('cancelled')} data-testid={`outcome-cancelled-${p.id}`}>❌ Cancelada</Button>
      <Button size="sm" variant="outline" onClick={() => setOutcome('reschedule')} data-testid={`outcome-reschedule-${p.id}`}>🔁 Remarcar</Button>
      {outcome && <OutcomeDialog p={p} outcome={outcome} onClose={() => setOutcome(null)} onDone={() => { setOutcome(null); setComposer(true); }} />}
      {composer && <RescheduleComposer pid={p.id} company={p.company} onClose={() => { setComposer(false); onChanged?.(); }} onSent={onChanged} />}
    </div>
  );
};

export const QuickNoShow = ({ p, onChanged }) => {
  const [outcome, setOutcome] = useState(null);
  const [composer, setComposer] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-rose-50 p-2" data-testid={`quick-no-show-${p.id}`}>
      <span className="text-xs font-bold text-rose-800">Passaram 10 min — a empresa entrou?</span>
      <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => setOutcome('no_show')} data-testid={`quick-outcome-no-show-${p.id}`}>🚫 Não compareceu</Button>
      <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => setOutcome('reschedule')} data-testid={`quick-outcome-reschedule-${p.id}`}>🔁 Remarcar</Button>
      {outcome && <OutcomeDialog p={p} outcome={outcome} onClose={() => setOutcome(null)} onDone={() => { setOutcome(null); setComposer(true); }} />}
      {composer && <RescheduleComposer pid={p.id} company={p.company} onClose={() => { setComposer(false); onChanged?.(); }} onSent={onChanged} />}
    </div>
  );
};

export const bookingSpeedText = (m) => {
  const s = m?.booked_after_seconds;
  if (s === undefined || s === null) return null;
  const txt = s < 60 ? `${s} s` : s < 3600 ? `${Math.round(s / 60)} min` : s < 86400 ? `${Math.round(s / 3600)} h` : `${Math.round(s / 86400)} dia(s)`;
  return `Marcada ${txt} depois do email`;
};

export const BookingSpeed = ({ m, id }) => {
  const txt = bookingSpeedText(m);
  if (!txt) return null;
  return <span className="text-[11px] self-center" data-testid={`booking-speed-${id}`}>
    <span className="text-slate-500">{txt}</span>
    {m.booked_fast && <span className="ml-1 font-bold text-amber-700" data-testid={`booking-fast-${id}`}>⚠ Marcada muito depressa — pode ter sido um filtro automático</span>}
  </span>;
};

export const ProposeDateButton = ({ p, onChanged, label = 'Propor nova data' }) => {
  const [open, setOpen] = useState(false);
  return <>
    <Button size="sm" onClick={() => setOpen(true)} disabled={!p.email} data-testid={`propose-date-${p.id}`}><Send className="h-4 w-4 mr-1" />{label}</Button>
    {open && <RescheduleComposer pid={p.id} company={p.company} onClose={() => setOpen(false)} onSent={onChanged} />}
  </>;
};

export const ArchiveRescheduleButton = ({ p, onChanged }) => {
  const archive = async () => {
    if (!window.confirm('Arquivar sem propor nova data?')) return;
    try { await api.post(`/admin/prospects/${p.id}/reschedule/archive`); toast.success('Arquivado'); onChanged?.(); } catch (e) { toast.error(errText(e)); }
  };
  return <Button size="sm" variant="ghost" onClick={archive} data-testid={`archive-reschedule-${p.id}`}>Arquivar</Button>;
};

export const ConfirmManualButton = ({ p, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const m = p.meeting;
  if (!m?.start_at || m.confirmed_at || m.admin_confirmed_at || meetingPassed(p)) return null;
  const confirm = async (via) => {
    setBusy(true);
    try { await api.post(`/admin/prospects/${p.id}/meeting/confirm-manual`, { via }); toast.success('Reunião confirmada'); onChanged?.(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return <span className="inline-flex flex-wrap gap-2">
    <Button size="sm" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" onClick={() => confirm('phone')} data-testid={`confirm-phone-${p.id}`}>Confirmada por telefone</Button>
    <Button size="sm" disabled={busy} variant="outline" className="border-emerald-300 text-emerald-800" onClick={() => confirm('whatsapp')} data-testid={`confirm-whatsapp-${p.id}`}>Confirmada por WhatsApp</Button>
  </span>;
};

export const confirmationLabel = (m) => (m?.suspect_auto_click ? 'Por confirmar — possível clique automático' : m?.confirmed_at ? 'Presença confirmada pela empresa ✓' : m?.admin_confirmed_at ? `Confirmada por ${m.admin_confirmed_via === 'whatsapp' ? 'WhatsApp' : 'telefone'} ✓` : 'Aguarda confirmação');
